import { FC } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import { FaUserEdit, FaUserMinus } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { ReportUserListFieldsFragment, useReportUsersQuery } from "app/api/graph/types";
import { reportGraphId } from "app/api/graph/helpers";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { reportRoleLabel } from "app/api/graph/strings";
import { BlockButtonGroup } from "app/component/button/BlockButtonGroup";
import { ButtonLink } from "app/component/button/ButtonLink";
import { editReportUserRoute, removeReportUserRoute } from "app/route/Routes.tsx";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { useReportIdPermissions } from "app/api/graph/hook/use-report-permissions";

type TableRowProps = {
    reportId: number;
    reportUser: ReportUserListFieldsFragment;
    canRemove: boolean;
};

const TableRow = ({ reportUser, canRemove, reportId }: TableRowProps) => {
    const permissions = useGeneralPermissions(reportUser.permissions);

    return (
        <tr>
            <td>{reportUser.user.fullName}</td>
            <td>{reportRoleLabel(reportUser.role)}</td>
            <td>
                {permissions.canUpdate && (
                    <ButtonLink
                        variant="warning"
                        to={editReportUserRoute.build({ reportId, reportUserId: reportUser._id })}
                        size="sm"
                        title="Update"
                    >
                        <FaUserEdit />
                    </ButtonLink>
                )}
                {canRemove && (
                    <ButtonLink
                        variant="danger"
                        to={removeReportUserRoute.build({ reportId, userId: reportUser.user._id })}
                        size="sm"
                        title="Remove"
                    >
                        <FaUserMinus />
                    </ButtonLink>
                )}
            </td>
        </tr>
    );
};

type Props = {
    reportId: number;
};

export const ReportUsersTable: FC<Props> = ({ reportId }) => {
    const { data, loading, error, fetchMore } = useReportUsersQuery({
        variables: { reportId: reportGraphId(reportId) },
        notifyOnNetworkStatusChange: true,
    });
    const reportUsers = data?.report?.viewers;
    const { canRemoveUser } = useReportIdPermissions(reportGraphId(reportId));

    if (loading && !reportUsers) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!reportUsers) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const fetchMoreUsers = () => fetchMore({ variables: { after: reportUsers.pageInfo.endCursor } });

    return (
        <Table responsive="sm" bordered className="crud-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {mapNodes(reportUsers, (reportUser) => (
                    <TableRow
                        key={reportUser.id}
                        reportUser={reportUser}
                        reportId={reportId}
                        canRemove={canRemoveUser}
                    />
                ))}
                {reportUsers.edges?.length === 0 ? (
                    <tr>
                        <td colSpan={100}>No users.</td>
                    </tr>
                ) : null}
                {loading ? (
                    <tr>
                        <td colSpan={100}>
                            <LoadingSpinner />
                        </td>
                    </tr>
                ) : null}
                {reportUsers.pageInfo.hasNextPage ? (
                    <tr>
                        <td colSpan={100}>
                            <BlockButtonGroup>
                                <Button variant="light" onClick={fetchMoreUsers}>
                                    Load more
                                </Button>
                            </BlockButtonGroup>
                        </td>
                    </tr>
                ) : null}
            </tbody>
        </Table>
    );
};
